import api from "./api";

const rbacService = {
  // ==========================================
  // GLOBAL/DEFAULT ROLES (Super Admin Level)
  // These are the default templates that get copied to new institutes
  // ==========================================

  // Get default/global roles (used as templates for new institutes)
  getDefaultRoles: () => api.get("/rbac/default-roles"),

  // Create a default role template
  createDefaultRole: (roleData) => api.post("/rbac/default-roles", roleData),

  // Update a default role template
  updateDefaultRole: (id, roleData) =>
    api.put(`/rbac/default-roles/${id}`, roleData),

  // Delete a default role template
  deleteDefaultRole: (id) => api.delete(`/rbac/default-roles/${id}`),

  // Get default permissions matrix
  getDefaultPermissionsMatrix: () =>
    api.get("/rbac/default-permissions-matrix"),

  // Update default permissions matrix
  updateDefaultPermissionsMatrix: (matrix) =>
    api.put("/rbac/default-permissions-matrix", { matrix }),

  // ==========================================
  // INSTITUTE-SPECIFIC ROLES
  // When Super Admin enters an institute, these endpoints manage
  // the roles and permissions for that specific institute
  // ==========================================

  // Get roles for a specific institute
  getInstituteRoles: (instituteId, params) =>
    api.get(`/institutes/${instituteId}/rbac/roles`, { params }),

  // Get a specific role for an institute
  getInstituteRoleById: (instituteId, roleId) =>
    api.get(`/institutes/${instituteId}/rbac/roles/${roleId}`),

  // Create a new role for an institute
  createInstituteRole: (instituteId, roleData) =>
    api.post(`/institutes/${instituteId}/rbac/roles`, roleData),

  // Update a role for an institute
  updateInstituteRole: (instituteId, roleId, roleData) =>
    api.put(`/institutes/${instituteId}/rbac/roles/${roleId}`, roleData),

  // Delete a role for an institute
  deleteInstituteRole: (instituteId, roleId) =>
    api.delete(`/institutes/${instituteId}/rbac/roles/${roleId}`),

  // Clone a role within an institute
  cloneInstituteRole: (instituteId, roleId, newName) =>
    api.post(`/institutes/${instituteId}/rbac/roles/${roleId}/clone`, {
      newName,
    }),

  // Get permissions for a specific institute role
  getInstituteRolePermissions: (instituteId, roleId) =>
    api.get(`/institutes/${instituteId}/rbac/roles/${roleId}/permissions`),

  // Assign permissions to an institute role
  assignInstituteRolePermissions: (instituteId, roleId, permissions) =>
    api.post(`/institutes/${instituteId}/rbac/roles/${roleId}/permissions`, {
      permissions,
    }),

  // Reset institute roles to defaults (copies from global defaults)
  resetInstituteRolesToDefaults: (instituteId) =>
    api.post(`/institutes/${instituteId}/rbac/reset-to-defaults`),

  // Copy defaults to a new institute (called when institute is created)
  initializeInstituteRoles: (instituteId) =>
    api.post(`/institutes/${instituteId}/rbac/initialize`),

  // ==========================================
  // LEGACY/GENERAL ENDPOINTS
  // For backward compatibility
  // ==========================================

  // Roles (legacy - use getRoles from above for /role API)
  // Add timestamp param to avoid cached 304 responses after updates
  getRoleById: (id) => api.get(`/role/${id}`, { params: { t: Date.now() } }),

  cloneRole: (roleId, newName) =>
    api.post(`/rbac/roles/${roleId}/clone`, { newName }),

  // Permissions
  getPermissions: () => api.get("/rbac/permissions"),

  assignPermissions: (roleId, permissions) =>
    api.post(`/rbac/roles/${roleId}/permissions`, { permissions }),

  getRolePermissions: (roleId) => api.get(`/rbac/roles/${roleId}/permissions`),

  // Features
  getFeatures: () => api.get("/rbac/features"),

  assignFeatures: (roleId, features) =>
    api.post(`/rbac/roles/${roleId}/features`, { features }),

  getRoleFeatures: (roleId) => api.get(`/rbac/roles/${roleId}/features`),

  // Role Hierarchy
  getRoleHierarchy: () => api.get("/rbac/hierarchy"),

  updateRoleHierarchy: (hierarchy) => api.put("/rbac/hierarchy", { hierarchy }),

  // User Roles
  getUserRoles: (userId) => api.get(`/rbac/users/${userId}/roles`),

  assignUserRole: (userId, roleId) =>
    api.post(`/rbac/users/${userId}/roles`, { roleId }),

  removeUserRole: (userId, roleId) =>
    api.delete(`/rbac/users/${userId}/roles/${roleId}`),

  // Check Permissions
  checkPermission: (permission) => api.get(`/rbac/check/${permission}`),

  checkFeatureAccess: (feature) => api.get(`/rbac/check-feature/${feature}`),

  // ==========================================
  // ROLE CRUD (Super Admin - Dynamic Roles)
  // ==========================================

  // Get all roles from roles table
  getRoles: (params) => api.get("/role", { params }),

  getRoleByIdOrSlug: (idOrSlug) => api.get(`/role/${idOrSlug}`),

  createRole: (roleData) => api.post("/role", roleData),

  updateRole: (id, roleData) => api.put(`/role/${id}`, roleData),

  deleteRole: (id) => api.delete(`/role/${id}`),

  // Sync an institute override role back to its global template permissions
  // (route is available under both /role and /roles for compatibility)
  // Restore/sync = delete institute override diffs (falls back to global)
  syncGlobalRolePermissions: (roleId) => api.delete(`/roles/${roleId}/restore-global`),

  // ==========================================
  // NEW PERMISSION MANAGEMENT ENDPOINTS
  // ==========================================

  // Get current user's permissions (any authenticated user)
  // use timestamp param to avoid cached 304 responses without sending custom headers
  getMyPermissions: () => api.get("/permission/me", { params: { t: Date.now() } }),

  // Get all permissions
  getAllPermissions: () => api.get("/permission/all"),

  // Get permissions for a specific role
  getPermissionsForRole: (targetRole) => api.get(`/permission/role/${targetRole}`),

  // Assign permissions to a role
  assignPermissionsToRole: (roleId, permissions) =>
    api.post("/permission/assign", { roleId, permissions }),

  seedDefaultPermissions: (payload) => api.post("/permission/seed-defaults", payload || { replace: true }),

  // Get all roles with statistics (includeInactive for RBAC management UI)
  getAllRolesWithStats: (params) => api.get("/permission/roles", { params }),

  // add timestamp param to avoid stale 304 responses after updates
  getPermissionMatrix: () => api.get("/permission/matrix", { params: { t: Date.now() } }),

  // Bulk update permissions for multiple roles
  // payload: { rolePermissions: [{ role, permissionSlugs }], cascadeToInheritors?: boolean }
  bulkUpdatePermissions: (payload) => api.put("/permission/bulk-update", payload),

  getAllModules: () => api.get("/permission/modules"),

  createModule: (moduleData) => api.post("/permission/create-module", moduleData),

  // Export permissions (JSON format)
  exportPermissions: () => api.get("/permission/export"),

  // Import permissions (JSON format)
  importPermissions: (permissionsData) => api.post("/permission/import", permissionsData),

  // Create a new module with permissions
  createModule: (moduleData) => api.post("/permission/create-module", moduleData),
};

export default rbacService;
